import { Delete } from '@mui/icons-material';
import { Button } from '@mui/material';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { useAuth } from 'hooks';
import {
    getSupplierIcon,
    removeSupplierIcon,
    uploadSupplierIcon,
} from 'lib/firebase';
import React, { useEffect, useRef, useState } from 'react';
import styles from './SupplierIconUpload.module.scss';

export interface SupplierIconUploadProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/**
 * Drag and Drop file upload for supplier icons
 */
export function SupplierIconUpload({
    viewAsSupplier,
}: SupplierIconUploadProps) {
    const [loading, setLoading] = useState(true);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [filePicture, setFilePicture] = useState<string | null>(null);
    const { user } = useAuth();
    const supplierId = viewAsSupplier ?? user?.protected?.supplierId;

    useEffect(() => {
        setLoading(true);
        if (supplierId !== undefined)
            getSupplierIcon(supplierId)
                .then((iconUrl) => {
                    setFilePicture(iconUrl);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
    }, [supplierId]);

    if (supplierId === undefined)
        return (
            <div>
                You are not currently assigned a supplier, please reach out to
                support to be added to one
            </div>
        );

    if (loading) return <LoadingSpinner />;

    const handleDrag = function (e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = function (e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFilePicture(URL.createObjectURL(e.dataTransfer.files[0]));
        }
    };

    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            setFilePicture(URL.createObjectURL(e.target.files[0]));
            if (supplierId !== undefined) {
                uploadSupplierIcon(supplierId, e.target.files[0]);
            }
        }
    };

    const onButtonClick = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const handleRemoveFile = () => {
        setFilePicture(null);
        if (supplierId !== undefined) {
            removeSupplierIcon(supplierId);
        }
    };

    return (
        <div data-testid="SupplierIconUpload" className={styles.container}>
            {!filePicture && (
                <div
                    className={styles.uploadContainer}
                    onSubmit={(e) => e.preventDefault()}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <p className={styles.content}>
                        <input
                            aria-label="file-upload"
                            className={styles.fileInput}
                            ref={inputRef}
                            type="file"
                            onChange={handleChange}
                            accept=".jpg,.png"
                        />
                        Drag and drop your file here or{' '}
                        <span onClick={onButtonClick}>browse</span>
                    </p>
                </div>
            )}
            <>
                {filePicture && (
                    <>
                        <img
                            className={styles.filePicture}
                            src={filePicture}
                            alt="file given"
                        />
                        <div>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Delete />}
                                onClick={handleRemoveFile}
                                size="small"
                            >
                                Remove
                            </Button>
                        </div>
                    </>
                )}
            </>
        </div>
    );
}
